import React, { useEffect, useRef, useState } from "react";
import { SlidersHorizontal, ChevronDown, X } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const STAGES = {
  invitation_sent: "Invitation sent",
  confirmation_sent: "Confirmation sent",
  reminder_sent: "Reminder sent",
  not_sent: "No recorded email at current stage",
  history_unknown: "Unknown history",
};
const PANEL_ID = "client-detailed-filters";

export default function ClientOutreachFilters({
  hasEmail, visitYear, visitMonth, emailVisible, stage, programYear, onChange, onClear,
  quickActive = false, children,
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const chipRefs = useRef({});
  const pendingFocus = useRef(null);
  const inputClass = "w-full min-h-[44px] px-3 py-2 rounded-lg border text-sm bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#052F66]";

  const chips = [];
  if (stage) chips.push({ key: "stage", label: `Email stage: ${STAGES[stage] || stage}`, name: "Sharing Tree email stage", clear: { sharing_tree_stage: "" } });
  if (emailVisible && hasEmail) chips.push({ key: "email", label: hasEmail === "true" ? "Has email" : "No email", name: "Email on file", clear: { has_email: "" } });
  if (visitYear) chips.push({
    key: "visit",
    label: `Last visit: ${visitMonth ? `${MONTHS[Number(visitMonth) - 1] || visitMonth} ` : ""}${visitYear}`,
    name: "Last visit", clear: { last_visit_year: "", last_visit_month: "" },
  });
  const count = chips.length;
  const signature = chips.map((c) => c.key).join("|");

  useEffect(() => {
    const target = pendingFocus.current;
    if (!target) return;
    pendingFocus.current = null;
    const node = target !== "button" ? chipRefs.current[target] : null;
    (node || buttonRef.current)?.focus();
  }, [signature]);

  function removeChip(index) {
    const next = chips[index + 1] || chips[index - 1];
    pendingFocus.current = next ? next.key : "button";
    onChange(chips[index].clear);
  }
  function close() {
    setOpen(false);
    buttonRef.current?.focus();
  }
  function clearAll() {
    pendingFocus.current = null;
    buttonRef.current?.focus();
    onClear();
  }
  const showClear = count > 0 || quickActive;

  return (
    <div className="mt-3" data-testid="client-filter-toolbar">
      <div className="flex flex-wrap items-center gap-2">
        {children}
        <button
          ref={buttonRef} type="button" data-testid="filters-toggle-btn"
          aria-expanded={open} aria-controls={PANEL_ID}
          onClick={() => { if (open) close(); else setOpen(true); }}
          className="inline-flex items-center gap-1.5 min-h-[44px] sm:min-h-[36px] px-3 py-1.5 rounded-full border text-xs font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#052F66]"
          style={{ borderColor: "var(--fb-border)", background: open ? "#F1F5F9" : "white", color: "var(--fb-text)" }}
        >
          <SlidersHorizontal className="w-3.5 h-3.5" aria-hidden="true" />
          Filters{count > 0 ? ` (${count})` : ""}
          <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
        {showClear && (
          <button type="button" data-testid="clear-filters-btn" onClick={clearAll}
            className="min-h-[44px] sm:min-h-[36px] px-3 py-1.5 text-xs font-semibold rounded-full hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#052F66]"
            style={{ color: "var(--fb-text-muted)" }}>
            Clear filters
          </button>
        )}
      </div>

      {count > 0 && (
        <ul className="mt-2 flex flex-wrap gap-2" aria-label="Active filters" data-testid="active-filter-summaries">
          {chips.map((c, i) => (
            <li key={c.key} className="inline-flex items-center rounded-full text-xs font-semibold max-w-full"
              style={{ background: "#E0F2FE", color: "#075985" }}>
              <span className="pl-3 py-1.5 truncate">{c.label}</span>
              <button type="button" ref={(n) => { chipRefs.current[c.key] = n; }}
                data-testid={`remove-filter-${c.key}`}
                aria-label={`Remove ${c.name} filter`}
                onClick={() => removeChip(i)}
                className="min-w-[44px] sm:min-w-[32px] min-h-[36px] inline-flex items-center justify-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#052F66]">
                <X className="w-3 h-3" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div
        id={PANEL_ID} ref={panelRef} hidden={!open} role="group" aria-label="Detailed filters"
        data-testid="filters-panel"
        onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); close(); } }}
        className={`mt-3 p-4 rounded-xl border ${open ? "grid" : "hidden"} gap-4 sm:grid-cols-2 lg:grid-cols-3`}
        style={{ borderColor: "var(--fb-border)", background: "var(--fb-bg)" }}
      >
        <label className="flex flex-col gap-1 text-xs font-semibold min-w-0">
          Sharing Tree email stage{programYear ? ` (${programYear})` : " (current program year)"}
          <select aria-label="Sharing Tree email stage" className={inputClass} value={stage || ""}
            title="Recorded sends only. Signup alone is not a send; uncertain legacy records appear under Unknown history."
            onChange={(e) => onChange({ sharing_tree_stage: e.target.value })}>
            <option value="">All stages</option>
            {Object.entries(STAGES).map(([value, text]) => <option key={value} value={value}>{text}</option>)}
          </select>
          {stage === "not_sent" && <span className="font-normal">
            After signup, this means no recorded confirmation send; an earlier invitation may have been sent.
          </span>}
        </label>
        {emailVisible && <label className="flex flex-col gap-1 text-xs font-semibold min-w-0">
          Email on file
          <select aria-label="Email on file" className={inputClass} value={hasEmail}
            onChange={(e) => onChange({ has_email: e.target.value })}>
            <option value="">All clients</option>
            <option value="true">Has email</option>
            <option value="false">No email</option>
          </select>
        </label>}
        <fieldset className="grid grid-cols-2 gap-3 min-w-0 sm:col-span-2 lg:col-span-1">
          <legend className="text-xs font-semibold mb-1">Last visit</legend>
          <label className="flex flex-col gap-1 text-xs font-semibold min-w-0">
            Year
            <input aria-label="Last visit year" type="number" min="1900" max="9998"
              placeholder="Any year" className={inputClass} value={visitYear}
              onChange={(e) => onChange({ last_visit_year: e.target.value,
                ...(!e.target.value ? { last_visit_month: "" } : {}) })} />
          </label>
          <label className="flex flex-col gap-1 text-xs font-semibold min-w-0">
            Month
            <select aria-label="Last visit month" disabled={!visitYear}
              className={`${inputClass} disabled:opacity-50`} value={visitMonth}
              onChange={(e) => onChange({ last_visit_month: e.target.value })}>
              <option value="">All months</option>
              {MONTHS.map((month, i) => <option key={month} value={i + 1}>{month}</option>)}
            </select>
          </label>
        </fieldset>
      </div>
    </div>
  );
}
